<template>
    <CourseGroupForm
        v-if="isGroupForm && isCorporateGroupForm"
        :group-id="$route.query.id || null"
    />
    <SchoolCourseGroupForm
        v-else-if="isGroupForm"
        :group-id="$route.query.id || null"
    />
    <div
        v-else-if="needsTypeChoice"
        class="course-form text-start pb-8 pb-14"
    >
        <v-dialog :model-value="true" persistent max-width="520">
            <v-card rounded="lg" class="corporate-dialog edit-courses-type-dialog">
                <v-card-title class="corporate-dialog__header">
                    <div>
                        <h2 class="corporate-dialog__title">{{ $t('admin.courses.whatToCreate') }}</h2>
                        <p class="corporate-dialog__subtitle">
                            {{ $t('admin.courses.whatToCreateLead') }}
                        </p>
                    </div>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <div class="edit-courses-type-options">
                        <button
                            type="button"
                            class="edit-courses-type-option"
                            @click="chooseSimpleCourse"
                        >
                            <span class="edit-courses-type-option__icon" aria-hidden="true">
                                <v-icon>mdi-book-open-page-variant</v-icon>
                            </span>
                            <span class="edit-courses-type-option__text">
                                <strong>{{ $t('admin.courses.simpleCourse') }}</strong>
                                <small>{{ $t('admin.courses.simpleCourseHint') }}</small>
                            </span>
                        </button>
                        <button
                            type="button"
                            class="edit-courses-type-option"
                            @click="chooseCourseGroup"
                        >
                            <span class="edit-courses-type-option__icon" aria-hidden="true">
                                <fa icon="fa-layer-group" />
                            </span>
                            <span class="edit-courses-type-option__text">
                                <strong>{{ $t('admin.courses.courseGroup') }}</strong>
                                <small>{{ groupTypeHint }}</small>
                            </span>
                        </button>
                    </div>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-btn variant="text" class="text-none" @click="cancelTypeChoice">
                        {{ $t('common.cancel') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
    <div
        v-else
        class="course-form text-start pb-8 pb-14"
        data-aos="fade-left"
        data-aos-duration="1500"
    >
        <Loader v-if="isLoading" />
        <v-container v-else class="p-0" fluid>
            <div class="page-card">
                <h1 class="card-title mb-2">
                    <span v-if="!isEditing">Novo</span>
                    <span v-else>Editar</span>
                    curso
                </h1>
                <p class="card-subtitle mb-4 mb-md-5">
                    <span v-if="!isEditing">Crie</span
                    >{{ $t('admin.courseForm.editInfoLead') }} Após salvar, você poderá personalizar e criar os
                    módulos e atividades.
                </p>
                <div
                    v-if="!isEditing"
                    class="d-flex justify-content-start mb-4"
                >
                    <v-btn
                        variant="tonal"
                        color="primary"
                        class="text-none"
                        prepend-icon="mdi-auto-fix"
                        @click="fillDemoData"
                    >
                        Preencher dados
                    </v-btn>
                </div>
                <hr class="page-card__divider" />
                <v-row class="mt-2 mt-md-4">
                    <v-col cols="12" md="4">
                        <label for="nameInput" class="labeld required"
                            >Nome do curso</label
                        >
                        <input
                            id="nameInput"
                            type="text"
                            class="inputd"
                            v-model="courseData.name"
                            placeholder="Digite o nome do curso"
                            required
                        />
                    </v-col>

                    <v-col cols="12" md="4">
                        <label for="comercialNameInput" class="labeld required"
                            >Nome do comercial</label
                        >
                        <input
                            id="comercialNameInput"
                            type="text"
                            class="inputd"
                            v-model="courseData.commercialName"
                            placeholder="Digite o nome do comercial"
                            required
                        />
                    </v-col>

                    <v-col cols="12" md="4">
                        <label for="urlInput" class="labeld required"
                            >URL do curso</label
                        >
                        <input
                            id="urlInput"
                            type="text"
                            class="inputd"
                            v-model="courseData.url"
                            placeholder="Digite a URL do curso"
                            required
                        />
                    </v-col>

                    <v-col cols="12">
                        <div class="course-form-toggle course-form-toggle--compact">
                            <label for="priceEnabledToggle" class="labeld"
                                >{{ $t('admin.courseForm.setPrice') }}</label
                            >
                            <CustomToggle
                                id="priceEnabledToggle"
                                :model-value="courseData.priceEnabled"
                                @update:model-value="onPriceEnabledChange"
                            />
                        </div>
                    </v-col>

                    <template v-if="courseData.priceEnabled">
                        <v-col cols="6" md="3">
                            <label for="priceInput" class="labeld"
                                >{{ $t('admin.courseForm.optionalPrice') }}</label
                            >
                            <div class="append-input-group">
                                <div class="append-input-group__text">R$</div>
                                <input
                                    id="priceInput"
                                    type="text"
                                    class="inputd"
                                    :value="
                                        courseData.price
                                            ? courseData.price
                                                  .toString()
                                                  .replace('.', ',')
                                            : ''
                                    "
                                    placeholder="0,00"
                                    pattern="[0-9,]*"
                                    inputmode="decimal"
                                    @input="onChangeCurrency('price', $event)"
                                />
                            </div>
                        </v-col>

                        <v-col cols="6" md="3">
                            <label for="courseDiscountInput" class="labeld"
                                >Desconto (opcional)</label
                            >
                            <div class="append-input-group">
                                <div class="append-input-group__text">R$</div>
                                <input
                                    id="courseDiscountInput"
                                    type="text"
                                    class="inputd"
                                    :value="
                                        courseData.discount
                                            ? courseData.discount
                                                  .toString()
                                                  .replace('.', ',')
                                            : ''
                                    "
                                    placeholder="0,00"
                                    pattern="[0-9,]*"
                                    inputmode="decimal"
                                    @input="onChangeCurrency('discount', $event)"
                                />
                            </div>
                        </v-col>

                        <v-col cols="6" md="3">
                            <label for="courseFinalValueInput" class="labeld"
                                >Valor Final</label
                            >
                            <div class="append-input-group">
                                <div class="append-input-group__text">R$</div>
                                <input
                                    id="courseFinalValueInput"
                                    type="text"
                                    class="inputd"
                                    :value="finalPriceDisplay"
                                    placeholder="0,00"
                                    readonly
                                />
                            </div>
                        </v-col>

                        <v-col cols="6" md="3">
                            <label for="installment" class="labeld"
                                >{{ $t('admin.courseForm.optionalInstallments') }}</label
                            >
                            <select
                                name=""
                                id="installment"
                                class="form-select inputd"
                                v-model="courseData.installment"
                            >
                                <option v-for="n in 12" :key="n" :value="n">
                                    {{ n }}
                                </option>
                            </select>
                        </v-col>
                    </template>

                    <v-col cols="12" md="6">
                        <label class="labeld" for="shortDescriptionTextarea"
                            >{{ $t('common.description') }}</label
                        >
                        <textarea
                            id="shortDescriptionTextarea"
                            class="inputd"
                            v-model="courseData.shortDescription"
                            :placeholder="$t('admin.courseForm.shortDescriptionPlaceholder')"
                            rows="4"
                        ></textarea>
                    </v-col>

                    <v-col cols="12" md="6">
                        <label
                            class="labeld"
                            for="courseCompleteDescriptionTextarea"
                            >{{ $t('admin.courseForm.fullDescription') }}</label
                        >
                        <textarea
                            id="courseCompleteDescriptionTextarea"
                            class="inputd"
                            v-model="courseData.longDescription"
                            :placeholder="$t('admin.courseForm.descriptionPlaceholder')"
                            rows="4"
                        ></textarea>
                    </v-col>

                    <v-col cols="12" md="4">
                        <div class="course-form-toggle mb-2">
                            <label for="approvalEnabledToggle" class="labeld"
                                >{{ $t('admin.courseForm.enableApproval') }}</label
                            >
                            <CustomToggle
                                :id="'approvalEnabledToggle'"
                                v-model="courseData.approvalEnabled"
                            />
                        </div>
                        <div>
                            <label for="courseMinGradeSelect" class="labeld"
                                >{{ $t('admin.courseForm.minGrade') }}</label
                            >
                            <select
                                name=""
                                id="courseMinGradeSelect"
                                class="form-select inputd"
                                v-model="courseData.minGrade"
                                :disabled="!courseData.approvalEnabled"
                            >
                                <option disabled value="0">0</option>
                                <option v-for="n in 20" :key="n" :value="n / 2">
                                    {{ n / 2 }}
                                </option>
                            </select>
                        </div>
                    </v-col>

                    <v-col cols="12" md="4" class="px-md-6">
                        <div class="course-form-toggle mb-2">
                            <label for="certificateEnabledToggle" class="labeld"
                                >{{ $t('admin.courseForm.hasCertificate') }}</label
                            >
                            <CustomToggle
                                :id="'certificateEnabledToggle'"
                                v-model="courseData.certificateEnabled"
                            />
                        </div>

                        <div>
                            <label for="statusSelect" class="labeld required"
                                >Status</label
                            >
                            <select
                                id="statusSelect"
                                class="form-select inputd"
                                v-model="courseData.status"
                                required
                            >
                                <option value="1">Ativo</option>
                                <option value="2">
                                    Apenas matrículas internas
                                </option>
                                <option value="3">Em Espera</option>
                                <option value="4">Inativo</option>
                            </select>
                        </div>
                    </v-col>

                    <v-col
                        cols="12"
                        md="4"
                        class="d-md-flex flex-md-column justify-content-md-end"
                    >
                        <label
                            for="courseAvaliabilityDeadlineSelect"
                            class="labeld required"
                        >
                            Prazo para Disponibilidade
                        </label>

                        <select
                            id="courseAvaliabilityDeadlineSelect"
                            class="form-select inputd"
                            v-model="courseData.availabilityDeadline"
                            required
                        >
                            <option value="1">1 dia</option>
                            <option value="2">2 dias</option>
                            <option value="3">3 dias</option>
                            <option value="4">4 dias</option>
                            <option value="5">5 dias</option>
                            <option value="6">6 dias</option>
                            <option value="7">7 dias</option>
                            <option value="8">8 dias</option>
                            <option value="9">9 dias</option>
                            <option value="10">10 dias</option>
                            <option value="20">20 dias</option>
                            <option value="30">30 dias</option>
                            <option value="40">40 dias</option>
                            <option value="50">50 dias</option>
                            <option value="60">60 dias</option>
                            <option value="70">70 dias</option>
                            <option value="80">80 dias</option>
                            <option value="90">90 dias</option>
                            <option value="120">4 meses</option>
                            <option value="150">5 meses</option>
                            <option value="180">6 meses</option>
                            <option value="210">7 meses</option>
                            <option value="240">8 meses</option>
                            <option value="270">9 meses</option>
                            <option value="300">10 meses</option>
                            <option value="330">11 meses</option>
                            <option value="365">12 meses</option>
                            <option value="0">{{ $t('admin.courseForm.lifelong') }}</option>
                        </select>
                    </v-col>

                    <v-col cols="12" md="12">
                        <label
                            for="courseCoverImageSelect"
                            class="labeld required"
                            >Capa do Produto - Principal</label
                        >
                        <CustomFileInput
                            id="courseCoverImageSelect"
                            v-model="coverImage.file"
                            height="160"
                            :can-delete="false"
                            infoText="Selecione uma imagem no formato JPG ou PNG"
                        />
                        <p class="helper-text mt-1">
                            Tamanho recomendado: 1080x400 pixels
                        </p>
                    </v-col>

                    <v-col cols="12" md="6" v-if="false">
                        <label
                            for="courseThumbnailSelect"
                            class="labeld required"
                            >Capa do Produto - Thumbnail</label
                        >
                        <CustomFileInput
                            id="courseThumbnailSelect"
                            v-model="thumbnailImage.file"
                            height="160"
                            :can-delete="false"
                            infoText="Selecione uma imagem no formato JPG ou PNG"
                        />
                        <p class="helper-text mt-1">
                            Tamanho recomendado: 1440x768 pixels (proporção
                            16:9)
                        </p>
                    </v-col>

                    <v-col
                        cols="12"
                        class="d-flex justify-content-center justify-content-md-end mt-5 mt-md-6"
                    >
                        <v-btn
                            variant="tonal"
                            color="grey-light-4"
                            :size="$vuetify.display.xs ? 'default' : 'large'"
                            class="mr-4"
                            @click="$router.go(-1)"
                        >
                            {{ $t('common.cancel') }}
                        </v-btn>
                        <v-btn
                            variant="flat"
                            color="primary"
                            :size="$vuetify.display.xs ? 'default' : 'large'"
                            :min-width="$vuetify.display.xs ? '120px' : '140px'"
                            @click="saveCourse"
                        >
                            Salvar
                        </v-btn>
                    </v-col>
                </v-row>
            </div>
        </v-container>
    </div>
</template>

<script>
    import { mapState, mapActions } from 'vuex';
    import apiAWS from '@/utils/storage/aws';
    import apiCourses from '@/utils/courses';
    import { toast } from 'vue3-toastify';
    import CustomToggle from '@/components/Form/CustomToggle.vue';
    import CustomFileInput from '@/components/Form/CustomFileInput.vue';
    import Loader from '@/components/Weflix/Loader';
    import CourseGroupForm from '@/components/Admin/Corporate/CourseGroupForm.vue';
    import SchoolCourseGroupForm from '@/components/Admin/SchoolCourseGroupForm.vue';
    import { isAdminCorporateMode } from '@/utils/corporate/adminDashboardMode';

    export default {
        components: {
            CustomToggle,
            CustomFileInput,
            Loader,
            CourseGroupForm,
            SchoolCourseGroupForm,
        },
        data() {
            return {
                isLoading: false,
                courseData: {
                    name: '',
                    commercialName: '',
                    url: '',
                    status: '1',
                    price: null,
                    discount: 0,
                    priceEnabled: false,
                    shortDescription: '',
                    longDescription: '',
                    approvalEnabled: false,
                    minGrade: 0,
                    certificateEnabled: false,
                    availabilityDeadline: 1,
                    coverImage: null,
                    thumbnailImage: null,
                    installment: 1,
                },
                coverImage: {
                    file: null,
                    path: '',
                    finalPath: '',
                },
                thumbnailImage: {
                    file: null,
                    path: '',
                    finalPath: '',
                },
            };
        },
        watch: {
            'courseData.name'(newVal, oldVal) {
                const commercial = this.courseData.commercialName;
                if (!commercial || commercial === oldVal) {
                    this.courseData.commercialName = newVal;
                }
                this.courseData.url = this.formatUrl(newVal);
            },
            'courseData.approvalEnabled'(newVal) {
                if (!newVal) {
                    this.courseData.minGrade = 0;
                }
            },
        },
        computed: {
            ...mapState([
                'token',
                'corporateCompanySettings',
                'corporateEnabled',
                'company',
                'adminDashboardMode',
            ]),
            isGroupForm() {
                return this.$route.query.kind === 'group';
            },
            isCorporateGroupForm() {
                return isAdminCorporateMode();
            },
            groupTypeHint() {
                return this.isCorporateGroupForm
                    ? this.$t('admin.courses.courseGroupHintCorporate')
                    : this.$t('admin.courses.courseGroupHintSchool');
            },
            isEditing() {
                return !!this.$route.params.id;
            },
            courseId() {
                return this.$route.params.id || null;
            },
            needsTypeChoice() {
                if (this.isEditing || this.isGroupForm) return false;
                if (this.$route.query.kind === 'simple') return false;
                return true;
            },
            finalPriceDisplay() {
                const price = Number(this.courseData.price) || 0;
                const discount = Number(this.courseData.discount) || 0;
                return Math.max(0, price - discount)
                    .toFixed(2)
                    .replace('.', ',');
            },
        },
        beforeMount() {
            if (!this.isGroupForm && !this.needsTypeChoice && this.isEditing) {
                this.getCourseData();
            }
        },
        methods: {
            ...mapActions(['storeCurrentCollections']),
            onPriceEnabledChange(value) {
                this.courseData.priceEnabled = !!value;
                if (!value) {
                    this.courseData.price = null;
                    this.courseData.discount = 0;
                    this.courseData.installment = 1;
                }
            },
            chooseSimpleCourse() {
                this.$router.replace({
                    path: '/dashboard/form-curso',
                    query: { kind: 'simple' },
                });
            },
            chooseCourseGroup() {
                this.$router.replace({
                    path: '/dashboard/form-curso',
                    query: { kind: 'group' },
                });
            },
            cancelTypeChoice() {
                this.$router.push('/dashboard/cursos');
            },
            async fillDemoData() {
                const stamp = Date.now().toString().slice(-5);
                const name = `Curso demonstração ${stamp}`;

                this.courseData = {
                    ...this.courseData,
                    name,
                    commercialName: name,
                    url: this.formatUrl(name),
                    status: '1',
                    priceEnabled: true,
                    price: '199.90',
                    discount: '20.00',
                    installment: 3,
                    shortDescription:
                        'Curso introdutório com conceitos práticos e exercícios guiados para o dia a dia.',
                    longDescription:
                        'Neste curso você aprende os fundamentos com aulas objetivas, exemplos reais e atividades para fixar o conteúdo. Ideal para quem quer começar rápido e com acompanhamento.',
                    approvalEnabled: true,
                    minGrade: 7,
                    certificateEnabled: true,
                    availabilityDeadline: 90,
                };

                try {
                    const coverFile = await this.createDemoImageFile(
                        `${name} capa`,
                        1080,
                        400,
                        'curso-capa-demo.png',
                    );
                    this.coverImage.file = coverFile;
                    this.thumbnailImage.file = coverFile;
                } catch (error) {
                    console.warn('[course-form] falha ao gerar imagens demo', error);
                }

                this.notify(this.$t('admin.courseForm.demoFilled'), 'success');
            },
            createDemoImageFile(label, width, height, filename) {
                return new Promise((resolve, reject) => {
                    const canvas = document.createElement('canvas');
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Canvas não disponível'));
                        return;
                    }

                    const gradient = ctx.createLinearGradient(0, 0, width, height);
                    gradient.addColorStop(0, '#160266');
                    gradient.addColorStop(1, '#4c3bcf');
                    ctx.fillStyle = gradient;
                    ctx.fillRect(0, 0, width, height);

                    ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
                    ctx.font = `600 ${Math.max(28, Math.round(width / 18))}px sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(label.slice(0, 28), width / 2, height / 2);

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Falha ao gerar imagem'));
                            return;
                        }
                        resolve(
                            new File([blob], filename, {
                                type: 'image/png',
                                lastModified: Date.now(),
                            }),
                        );
                    }, 'image/png');
                });
            },
            async getCourseData() {
                this.isLoading = true;
                try {
                    if (this.courseId) {
                        const response = await apiCourses.getCourse(
                            this.courseId,
                            this.token
                        );

                        this.courseData.name = response.name || '';
                        this.courseData.commercialName =
                            response.comercialName || '';
                        this.courseData.url = response.url || '';
                        this.courseData.status = response.status
                            ? response.status.toString()
                            : '1';
                        this.courseData.price =
                            response.price != null
                                ? this.formatCurrency(response.price * 100)
                                : null;
                        this.courseData.discount =
                            response.discount != null
                                ? this.formatCurrency(response.discount * 100)
                                : 0;
                        this.courseData.priceEnabled =
                            (Number(response.price) || 0) > 0
                            || (Number(response.discount) || 0) > 0;
                        this.courseData.shortDescription =
                            response.shortDescription || '';
                        this.courseData.longDescription =
                            response.longDescription || '';
                        this.courseData.approvalEnabled = !!response.approved;
                        this.courseData.minGrade = response.approved || 0;
                        this.courseData.certificateEnabled =
                            !!response.certificate;
                        this.courseData.availabilityDeadline =
                            response.dayExpired || 0;

                        this.coverImage.path = response.imagePath || '';
                        this.coverImage.finalPath =
                            response.imageFinalPath || '';
                        this.coverImage.file = response.imageFinalPath || null;

                        this.thumbnailImage.path = response.imagePath2 || '';
                        this.thumbnailImage.finalPath =
                            response.imageFinalPath2 || '';
                        this.thumbnailImage.file =
                            response.imageFinalPath2 || null;
                    }
                } catch (error) {
                    this.$router.push({
                        path: '/dashboard/cursos',
                    });
                    this.notify('Erro ao carregar os dados do curso.', 'error');
                } finally {
                    this.isLoading = false;
                }
            },
            onChangeCurrency(field, event) {
                const value = event.target.value.replace(/\D/g, '');
                this.courseData[field] = this.formatCurrency(value);
            },
            formatCurrency(value) {
                const formmatedValue = (Number(value) / 100).toFixed(2);
                return formmatedValue;
            },
            formatUrl(value) {
                const formattedUrl = value
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/ç/g, 'c')
                    .replace(/[^a-z0-9\s]/g, '')
                    .replace(/\s+/g, '-')
                    .trim();

                return formattedUrl;
            },
            validateRequiredFields() {
                const requiredFields = [
                    { key: 'name', label: 'Nome do curso' },
                    { key: 'commercialName', label: 'Nome do comercial' },
                    { key: 'url', label: 'URL do curso' },
                    { key: 'status', label: 'Status' },
                    {
                        key: 'availabilityDeadline',
                        label: 'Prazo para Disponibilidade',
                    },
                ];
                for (const field of requiredFields) {
                    if (
                        this.courseData[field.key] === null ||
                        this.courseData[field.key] === '' ||
                        typeof this.courseData[field.key] === 'undefined'
                    ) {
                        this.notify(
                            this.$t('admin.courseForm.fillRequiredField', { field: field.label }),
                            'error'
                        );
                        return false;
                    }
                }

                if (!this.coverImage.file) {
                    this.notify(
                        this.$t('admin.courseForm.fillRequiredCover'),
                        'error'
                    );
                    return false;
                }

                return true;
            },

            async saveCourse() {
                if (!this.validateRequiredFields()) return;

                this.isLoading = true;

                try {
                    await this.saveImages();

                    const payload = {
                        name: this.courseData.name,
                        comercialName: this.courseData.commercialName,
                        url: this.courseData.url,
                        status: this.courseData.status,
                        price: this.courseData.priceEnabled
                            ? (Number(this.courseData.price) || 0)
                            : 0,
                        discount: this.courseData.priceEnabled
                            ? (Number(this.courseData.discount) || 0)
                            : 0,
                        shortDescription: this.courseData.shortDescription,
                        longDescription: this.courseData.longDescription,
                        certificate: this.courseData.certificateEnabled ? 1 : 0,
                        imagePath: this.coverImage.path,
                        imagePath2: this.coverImage.path,
                        dayExpired: this.courseData.availabilityDeadline,
                        approved: this.courseData.approvalEnabled
                            ? this.courseData.minGrade
                            : -1,
                        installment: this.courseData.priceEnabled
                            ? (Number(this.courseData.installment) || 1)
                            : 1,
                        order: 0,
                    };

                    let response;
                    if (this.isEditing) {
                        response = await apiCourses.editCourse(
                            this.courseId,
                            payload,
                            this.token
                        );
                    } else {
                        response = await apiCourses.createNewCourse(
                            payload,
                            this.token
                        );
                    }

                    this.storeCurrentCollections({
                        currentCollections: response,
                    });
                    this.$router.push({
                        path: '/dashboard/curso',
                        query: { toast: 'Curso criado com sucesso!' },
                    });
                } catch (error) {
                    console.error('Erro ao salvar curso:', error);
                    this.notify('Erro ao tentar salvar o curso', 'error');
                } finally {
                    this.isLoading = false;
                }
            },
            async saveImages() {
                if (
                    this.coverImage.file &&
                    this.coverImage.file instanceof File
                ) {
                    this.coverImage.path = await this.saveImageAws(
                        this.coverImage.file
                    );
                }

                this.thumbnailImage.path = this.coverImage.path;
                this.thumbnailImage.file = this.coverImage.file;
                this.thumbnailImage.finalPath = this.coverImage.finalPath;
            },
            async saveImageAws(file) {
                try {
                    const response = await apiAWS.getSignatureImage(
                        this.token,
                        file.name
                    );
                    await this.putFileAws(response.url, file);
                    return response.token;
                } catch (error) {
                    this.notify('Erro ao gerar token da imagem.', 'error');
                }
            },
            async putFileAws(url, file) {
                try {
                    const response = await apiAWS.putFileAWS(url, file);
                    return response;
                } catch (error) {
                    this.notify('Erro ao salvar a imagem.', 'error');
                }
            },
            notify(message, type = 'info') {
                toast(message, {
                    autoClose: 3000,
                    position: 'top-right',
                    theme: 'light',
                    limit: 1,
                    type: type,
                    dangerouslyHTMLString: true,
                });
            },
        },
    };
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.edit-courses-type-options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.edit-courses-type-option {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    width: 100%;
    padding: 0.95rem 1rem;
    border: 1px solid #e4e7ef;
    border-radius: 12px;
    background: #fff;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.edit-courses-type-option:hover {
    border-color: #aeb6d4;
    box-shadow: 0 2px 10px rgba(37, 8, 145, 0.08);
}

.edit-courses-type-option__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 10px;
    background: #eef1fb;
    color: #3b3f8c;
    flex-shrink: 0;
}

.edit-courses-type-option__icon :deep(svg) {
    width: 1.15rem;
    height: 1.15rem;
    color: #3b3f8c !important;
}

.edit-courses-type-option__text {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}

.edit-courses-type-option__text strong {
    font-size: 0.98rem;
    color: #1a1d26;
}

.edit-courses-type-option__text small {
    color: #6b7280;
    font-size: 0.82rem;
    line-height: 1.35;
}

.course-form-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-height: 24px;
}

.course-form-toggle--compact {
    max-width: 22rem;
}

.course-form-toggle .labeld {
    margin: 0;
    line-height: 1.25;
}
</style>
